/**
 * Turning a stored user key into a person's name.
 *
 * The repository stores WHO as a key — a login, a user id, whatever the host
 * authenticates with — because that is the only thing that stays stable when
 * somebody is renamed. Every screen was then showing that key: "checked out by
 * mwebb", "Sent by u_4f21c0", an audit column of identifiers. It is the right
 * thing to store and the wrong thing to show.
 *
 * `listUsers` is the directory. It is OPTIONAL on the client, so everything
 * here degrades to the key rather than to a blank: a departed owner whose key
 * no longer resolves is still a fact about the document, and hiding it would
 * make the document look unowned.
 */

import { useEffect, useState } from 'react'
import type { DmsClient, DmsUser } from './contracts'

/**
 * Anything that is only ever a machine identifier: a UUID, or a long opaque
 * hex/id string. Showing one of these to a reader is worse than showing
 * nothing — "Looked after by 9d766f1f-50c6-4668-bb17-eabebe25e8a7" answers the
 * question with noise, and the reader cannot tell it is a person at all.
 */
function looksLikeAnId(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
    || /^[0-9a-f]{24,}$/i.test(value)
}

/**
 * The person's name.
 *
 * Falls back to the KEY when the directory cannot place it — a login or an
 * email still tells a reader who is meant, and an owner who has left is a fact
 * about the document. But an unresolved opaque id returns EMPTY, so callers
 * drop the clause rather than printing a UUID at somebody. The directory only
 * lists people who hold a role on the project, so a document filed by someone
 * outside it lands here routinely.
 */
export function personName(users: DmsUser[] | undefined,
                           key: string | null | undefined): string {
  const raw = String(key || '').trim()
  if (!raw) return ''
  const found = users?.find((user) => user.user_key === raw)
  const name = String(found?.name || '').trim()
  if (name && name !== raw) return name
  return looksLikeAnId(raw) ? '' : raw
}

/**
 * A stored key rendered for a reader, with no directory to hand.
 *
 * The same rule `personName` ends on, for callers that resolved the name
 * themselves (a worklist row looks its people up once for the whole list, not
 * per row): a key a person can read — a login, an email — is shown as it is,
 * an opaque id is shown as nothing.
 */
export function readablePerson(key: string | null | undefined): string {
  const raw = String(key || '').trim()
  return raw && !looksLikeAnId(raw) ? raw : ''
}

/** `personName`, plus "you" when it is the person reading. */
export function personNameOrYou(users: DmsUser[] | undefined,
                                key: string | null | undefined,
                                userKey?: string): string {
  const raw = String(key || '').trim()
  if (!raw) return ''
  if (userKey && raw === userKey) return 'you'
  return personName(users, raw)
}

/**
 * The directory, loaded once per view.
 *
 * Every consumer needs the same list, and it changes about as often as the
 * staff do — so this is a plain fetch on mount rather than anything cleverer.
 * A host without `listUsers` gets `undefined` for ever, which every caller
 * already handles as "show the key".
 */
export function usePeople(client: DmsClient): DmsUser[] | undefined {
  const [users, setUsers] = useState<DmsUser[] | undefined>(undefined)
  useEffect(() => {
    if (!client.listUsers) return undefined
    let live = true
    void (async () => {
      try {
        const list = await client.listUsers!()
        if (live) setUsers(list)
      } catch {
        // The directory is a courtesy. Without it every screen shows keys,
        // which is what it did before this existed.
      }
    })()
    return () => { live = false }
  }, [client])
  return users
}
